import { useLocale } from '@lessonup/client-integration';
import { ParsedTextTipTapViewer, PollTextAnswerCard, PollTextAnswerContainer } from '@lessonup/pin-renderer';
import { PollPinTextAnswer } from '@lessonup/pins-shared';
import { PollPinAnswerDistribution } from '@lessonup/players-modern';
import { borderRadiusScaled, css, cssColors, cssColorsNew, spacing, styled } from '@lessonup/ui-components';
import { localeToIsoLocaleString } from '@lessonup/utils';
import React from 'react';

export interface TextPollAnswerAreaContentViewProps {
  answers: PollPinAnswerDistribution<PollPinTextAnswer>[];
  showResults: boolean;
}

export function TextPollAnswerAreaContentView({ answers, showResults }: TextPollAnswerAreaContentViewProps) {
  const locale = useLocale();
  const percentageFormatter = new Intl.NumberFormat(localeToIsoLocaleString(locale), {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (
    <PollTextAnswerContainer>
      {answers.map(({ answer, voteDistribution }, index) => (
        <PollTextAnswerCard key={`poll-answer-${answer.id}`} index={index} answer={answer}>
          <AnswerInputWrapper>
            <ParsedTextTipTapViewer content={answer.text} />
          </AnswerInputWrapper>
          <StyledResultValue show={showResults}>{percentageFormatter.format(voteDistribution)}</StyledResultValue>

          {voteDistribution > 0 && <StyledResultBar show={showResults} width={voteDistribution * 100} />}
        </PollTextAnswerCard>
      ))}
    </PollTextAnswerContainer>
  );
}

const AnswerInputWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const showAnimated = () => css`
  transition: all 0.3s ease;
`;

const StyledResultBar = styled.div<{ width: number; size?: number; show: boolean }>`
  position: absolute;
  opacity: 0.3;
  margin: 0 !important;
  left: ${() => `-${spacing.size8}`};
  top: ${() => `-${spacing.size8}`};
  ${({ show, width }) =>
    show
      ? css`
          width: calc(${width}% + 2 * ${spacing.size8});
        `
      : css`
          width: 0;
          visibility: hidden;
        `}

  height: calc(100% + 2 * ${spacing.size8});
  border-radius: ${() => {
    const radius = borderRadiusScaled(spacing.size8, 'small');
    return `0 ${radius} ${radius} 0`;
  }};
  border: solid 1px #dfe1e7;
  background: var(${cssColors.secondary.background?.cssVar});
  ${() => showAnimated()};
`;

/**
 * Styled component for displaying poll result percentages.
 * The 50px width is reserved so the 100% value always fits, even when not visible during the input phase.
 * This prevents the answer text from jumping around when toggling between input and results phases.
 */
const StyledResultValue = styled.div<{ show: boolean }>`
  position: relative;
  z-index: 2;
  font-weight: bold;
  font-size: 1rem;
  text-align: right;
  width: 50px;
  ${() => showAnimated()};
  ${({ show }) =>
    show
      ? css`
          visibility: visible;
        `
      : css`
          visibility: hidden;
        `}
  color: var(${cssColorsNew.surface.on?.cssVar});
`;
